import { Flex, Text } from '@mantine/core';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import {
	createSingleLineHeader,
	SINGLE_LINE_STYLE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';
import { RelatedPersonCardRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/relatedPeople.types.ts';
import { getRoleLabel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/relatedPeople.utils.ts';

const StackedCell = ({ value, subValue }: { value: string; subValue: string }) => (
	<Flex direction="column" justify="center" miw={0} w="100%">
		<Text size="B6Medium" c="dark.9" truncate style={SINGLE_LINE_STYLE}>
			{value}
		</Text>

		<Text fz={12} c="dark.5" truncate style={SINGLE_LINE_STYLE}>
			{subValue}
		</Text>
	</Flex>
);

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
}

export const getRelatedPeopleTableColumns = ({
	tMipManage,
}: Props): ExtendedColumnDef<RelatedPersonCardRow>[] => [
	createTextColumn<RelatedPersonCardRow>({
		id: 'name',
		accessorFn: (row) => row.displayName ?? '',
		cell: ({ row }) => (
			<StackedCell
				value={row.original.displayName ?? ''}
				subValue={row.original.email ?? ''}
			/>
		),
		header: createSingleLineHeader<RelatedPersonCardRow>(
			tMipManage('investors.details.related_people.columns.name')
		),
		columnLabel: tMipManage('investors.details.related_people.columns.name'),
		size: 220,
		minSize: 200,
	}),

	createTextColumn<RelatedPersonCardRow>({
		id: 'role',
		accessorFn: (row) => row.roles.map((role) => getRoleLabel(role, tMipManage)).join(', '),
		header: createSingleLineHeader<RelatedPersonCardRow>(
			tMipManage('investors.details.related_people.columns.role')
		),
		columnLabel: tMipManage('investors.details.related_people.columns.role'),
		size: 200,
		minSize: 160,
	}),

	createTextColumn<RelatedPersonCardRow>({
		id: 'taxInformation',
		accessorFn: (row) =>
			row.taxResidence
				.map(({ countryCode }) => getCountryNameByCode(countryCode) || countryCode)
				.join(', '),
		cell: ({ row }) =>
			row.original.taxResidence.length ? (
				<Flex direction="column" gap="0.25rem" miw={0} w="100%">
					{row.original.taxResidence.map(({ id, countryCode, number }) => (
						<StackedCell
							key={id}
							value={number ?? LONG_DASH}
							subValue={getCountryNameByCode(countryCode) || countryCode}
						/>
					))}
				</Flex>
			) : (
				<Text size="B6Medium" c="dark.9">
					{LONG_DASH}
				</Text>
			),
		header: createSingleLineHeader<RelatedPersonCardRow>(
			tMipManage('investors.details.related_people.columns.tax_information')
		),
		columnLabel: tMipManage('investors.details.related_people.columns.tax_information'),
		size: 200,
		minSize: 180,
	}),
];
